import { Check, GraduationCap, BookOpen, Briefcase, User, CheckCircle2, Quote } from "lucide-react";
import { Reveal } from "../components/Reveal";

const SectionHead = ({ label, title, sub }) => (
  <Reveal className="text-center max-w-2xl mx-auto">
    <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: "var(--accent)" }}>{label}</span>
    <h2 className="font-display mt-3 text-3xl md:text-4xl font-bold text-white leading-tight">{title}</h2>
    {sub && <p className="mt-4 text-base md:text-lg" style={{ color: "var(--text-body)" }}>{sub}</p>}
  </Reveal>
);

const learnItems = [
  "Which tech track actually fits you — AI, Cybersecurity, or Data Science",
  "Why most students struggle to get hired despite having a degree",
  "The real skills companies are hiring for in 2026 (not what colleges teach)",
  "How to build a job-ready profile in 6–12 months, even with zero prior experience",
  "Live Q&A — get your specific questions answered by industry practitioners",
];

export const LearnSection = () => (
  <section className="py-20 sm:py-28" style={{ background: "var(--bg-secondary)" }} data-testid="learn-section">
    <div className="max-w-3xl mx-auto px-5">
      <SectionHead label="What you'll learn" title="90 Minutes of Pure Career Clarity" />
      <div className="mt-12 space-y-4">
        {learnItems.map((item, i) => (
          <Reveal key={item} delay={i * 80}>
            <div className="rounded-xl p-5 flex items-start gap-4" style={{ background: "var(--bg-card)", border: "1px solid var(--input-border)" }} data-testid={`learn-item-${i}`}>
              <Check size={18} className="shrink-0 mt-0.5" style={{ color: "var(--accent)" }} strokeWidth={3} />
              <p className="text-base leading-relaxed" style={{ color: "var(--text-body)" }}>{item}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const audiences = [
  { icon: GraduationCap, title: "12th Pass-Outs", text: "Confused about which stream/career to pick after school" },
  { icon: BookOpen, title: "College Students", text: "Want real skills alongside your degree, not just theory" },
  { icon: Briefcase, title: "Graduates", text: "Feel unprepared for interviews and want a clear next step" },
];

export const AttendSection = () => (
  <section className="py-20 sm:py-28" style={{ background: "var(--bg-primary)" }} data-testid="attend-section">
    <div className="max-w-6xl mx-auto px-5">
      <SectionHead label="Who should attend" title="Built for Students at Every Crossroad" />
      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {audiences.map((a, i) => (
          <Reveal key={a.title} delay={i * 120}>
            <div className="track-card h-full rounded-2xl p-8 text-center" style={{ background: "var(--bg-card)", border: "1px solid var(--input-border)" }} data-testid={`attend-card-${i}`}>
              <div className="mx-auto w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: "rgba(14, 217, 181, 0.08)", color: "var(--accent)" }}>
                <a.icon size={22} strokeWidth={1.75} />
              </div>
              <h3 className="font-display mt-5 text-lg font-semibold text-white">{a.title}</h3>
              <p className="mt-2.5 text-sm leading-relaxed" style={{ color: "var(--text-body)" }}>{a.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const speakers = [
  { name: "Speaker Name", title: "Cybersecurity & AI Specialist · 10+ Years Industry Experience", bio: "Trainer profile to be confirmed — full bio will be shared with registered attendees." },
  { name: "Speaker Name", title: "Data Science Lead · Ex-MNC Analytics Teams", bio: "Trainer profile to be confirmed — full bio will be shared with registered attendees." },
  { name: "Speaker Name", title: "Cloud & Infrastructure Architect · Corporate Trainer", bio: "Trainer profile to be confirmed — full bio will be shared with registered attendees." },
];

export const SpeakersSection = () => (
  <section className="py-20 sm:py-28" style={{ background: "var(--bg-secondary)" }} data-testid="speakers-section">
    <div className="max-w-6xl mx-auto px-5">
      <SectionHead label="Meet your speakers" title="Learn From People Who Work in the Industry" />
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {speakers.map((s, i) => (
          <Reveal key={i} delay={i * 120}>
            <div className="track-card h-full rounded-2xl p-7 text-center" style={{ background: "var(--bg-card)", border: "1px solid var(--input-border)" }} data-testid={`speaker-card-${i}`}>
              <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center" style={{ background: "rgba(14, 217, 181, 0.08)", color: "var(--accent)" }}>
                <User size={26} strokeWidth={1.5} />
              </div>
              <h3 className="font-display mt-5 text-lg font-semibold text-white">{s.name}</h3>
              <p className="mt-1.5 text-sm font-medium" style={{ color: "var(--accent)" }}>{s.title}</p>
              <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--text-muted)" }}>{s.bio}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const agenda = [
  { time: "11:00 AM", item: "Welcome & Icebreaker" },
  { time: "11:10 AM", item: "Why 90% of Students Struggle to Get Hired (Industry Reality Check)" },
  { time: "11:35 AM", item: "Live Walkthrough: AI vs Cybersecurity vs Data Science — Which Fits You?" },
  { time: "12:05 PM", item: "Real Student Success Stories" },
  { time: "12:25 PM", item: "Live Q&A + Free Career Roadmap Offer for Attendees" },
];

export const AgendaSection = () => (
  <section className="py-20 sm:py-28" style={{ background: "var(--bg-primary)" }} data-testid="agenda-section">
    <div className="max-w-3xl mx-auto px-5">
      <SectionHead label="Seminar agenda" title="What Happens in the Room" />
      <div className="mt-14 relative">
        <div className="absolute left-[7px] top-2 bottom-2 w-px" style={{ background: "var(--input-border)" }} />
        <div className="space-y-8">
          {agenda.map((a, i) => (
            <Reveal key={a.item} delay={i * 100}>
              <div className="relative pl-10 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-6" data-testid={`agenda-item-${i}`}>
                <span className="absolute left-0 top-1.5 w-[15px] h-[15px] rounded-full" style={{ background: "var(--bg-card)", border: "2px solid var(--accent)" }} />
                <span className="font-display text-sm font-bold shrink-0 sm:w-20" style={{ color: "var(--accent)" }}>{a.time}</span>
                <p className="text-base leading-relaxed" style={{ color: "var(--text-body)" }}>{a.item}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
      <Reveal delay={200}>
        <p className="mt-10 text-center text-sm" style={{ color: "var(--text-muted)" }}>
          Timings indicative — final schedule shared with registered attendees.
        </p>
      </Reveal>
    </div>
  </section>
);

const trustBullets = ["100% Free, No Hidden Catch", "Hosted by Real Industry Practitioners", "Every Attendee Gets a Bonus Free Career Roadmap"];

export const WhySeminarSection = () => (
  <section className="py-20 sm:py-28" style={{ background: "var(--bg-secondary)" }} data-testid="why-seminar-section">
    <div className="max-w-3xl mx-auto px-5">
      <SectionHead label="Why attend" title="Not a Sales Webinar. An Honest Session." />
      <Reveal delay={150}>
        <div className="mt-12 rounded-2xl p-8 sm:p-10" style={{ background: "var(--bg-card)", border: "1px solid var(--input-border)" }} data-testid="why-seminar-quote">
          <Quote size={28} style={{ color: "var(--accent)" }} strokeWidth={1.5} />
          <p className="mt-5 text-base md:text-lg leading-relaxed text-white font-medium">
            "This isn't a sales webinar. It's an honest, practical session on what actually works — hosted by people
            who work in the industry, not marketers reading a script."
          </p>
          <ul className="mt-8 space-y-3">
            {trustBullets.map((b) => (
              <li key={b} className="flex items-center gap-3 text-sm sm:text-base" style={{ color: "var(--text-body)" }}>
                <CheckCircle2 size={18} style={{ color: "var(--accent)" }} className="shrink-0" />
                {b}
              </li>
            ))}
          </ul>
        </div>
      </Reveal>
    </div>
  </section>
);

const logos = ["EC-Council", "ISO Certified", "Skill India", "NSDC", "MSDE"];

export const SeminarTrustSection = () => (
  <section className="py-14 sm:py-16" style={{ background: "var(--bg-card)" }} data-testid="seminar-trust-section">
    <div className="max-w-6xl mx-auto px-5">
      <Reveal>
        <p className="text-center text-xs font-semibold tracking-widest uppercase" style={{ color: "var(--text-muted)" }}>
          Recognitions & Affiliations
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-5" data-testid="seminar-trust-logos">
          {logos.map((l) => (
            <span key={l} className="trust-logo font-display text-lg sm:text-xl font-bold tracking-wide">{l}</span>
          ))}
        </div>
        <p className="mt-8 text-center text-sm" style={{ color: "var(--text-body)" }} data-testid="seminar-trust-line">
          Trusted training partner recognized by 5,000+ students across India
        </p>
      </Reveal>
    </div>
  </section>
);
